<template>
  <form :novalidate="true" class="columns is-multiline" @submit.prevent="submit">
    <div class="column is-full">
      <h1 class="title is-6">
        <b-icon icon="sliders-h" size="is-small" class="mr-2" />
        Paramètres globaux
      </h1>
    </div>

    <div class="column is-full-mobile is-half">
      <b-field label="Alertes visibles">
        <div class="table-container pb-2 mb-0">
          <div class="field has-addons is-flex-wrap-nowrap mb-0">
            <b-checkbox-button
              v-for="type in alertTypes"
              :key="type.id"
              v-model="fields.privateAlertTypes"
              expanded
              :native-value="type.id"
            >
              <b-icon :icon="type.icon" size="is-small" />
              {{ type.label }}
            </b-checkbox-button>
          </div>
        </div>
      </b-field>
    </div>

    <b-field
      class="column is-full-mobile is-half"
      label="Format des coordonnées"
      :message="fields.globalCoordinateFormat.error"
      :type="fields.globalCoordinateFormat.error !== null ? 'is-danger' : ''"
    >
      <b-select
        v-model="fields.globalCoordinateFormat.value"
        expanded
        placeholder="Choisissez un format"
        icon="map-marker-alt"
      >
        <option v-for="format in coordinateFormats" :key="format.value" :value="format.value">
          {{ format.name }}{{ format.example }}
        </option>
      </b-select>
    </b-field>

    <b-field
      class="column is-full-mobile is-half"
      label="Seuil d'inactivité (min)"
      :message="fields.privateVehicleIdleThreshold.error || '0 ou vide = désactivé'"
      :type="fields.privateVehicleIdleThreshold.error !== null ? 'is-danger' : ''"
    >
      <b-numberinput
        v-model="fields.privateVehicleIdleThreshold.value"
        expanded
        placeholder="0"
        step="1"
        min="0"
        controls-position="compact"
        controls-alignment="right"
      />
    </b-field>

    <b-field class="column is-full-mobile is-half" label="Feed réseau social">
      <b-input
        v-model="fields.globalSocialFeedId"
        expanded
        placeholder="Identifiant du feed"
        icon="camera"
      />
    </b-field>

    <b-table
      class="column is-full"
      :data="tableData"
      :row-class="(row) => ({ 'has-background-white-ter is-hidden-mobile': row.isGroup })"
      mobile-cards
      hoverable
    >
      <b-table-column v-slot="{ row }" label="Fonctionnalité">
        <span
          v-if="row.isGroup"
          class="is-size-7 has-text-weight-semibold has-text-grey is-uppercase"
        >
          {{ row.label }}
        </span>
        <span v-else class="icon-text is-align-items-center">
          <b-icon :icon="row.icon" size="is-small" class="has-text-grey-light mr-2" />
          {{ row.label }}
        </span>
      </b-table-column>

      <b-table-column
        v-slot="{ row }"
        label="Public"
        centered
        header-class="has-background-success-light has-text-success"
        width="30%"
      >
        <template v-if="row.isGroup" />
        <template v-else-if="row.type === 'switch'">
          <b-switch v-if="row.publicKey" v-model="fields[row.publicKey]" type="is-success" />
          <span v-else class="has-text-grey-lighter">—</span>
        </template>

        <template v-else-if="row.type === 'checkbox'">
          <b-field
            :message="fields.publicMapLayers.error"
            :type="fields.publicMapLayers.error !== null ? 'is-danger' : ''"
          >
            <b-checkbox-button
              v-for="layer in mapLayers"
              :key="layer"
              v-model="fields.publicMapLayers.value"
              expanded
              :native-value="layer"
            >
              {{ layer }}
            </b-checkbox-button>
          </b-field>
        </template>

        <template v-else-if="row.type === 'select'">
          <b-field
            :message="fields.publicDefaultMapLayer.error"
            :type="fields.publicDefaultMapLayer.error !== null ? 'is-danger' : ''"
          >
            <b-select
              v-model="fields.publicDefaultMapLayer.value"
              expanded
              placeholder="Choisir"
              icon="map"
            >
              <option v-for="name in fields.publicMapLayers.value" :key="name" :value="name">
                {{ name }}
              </option>
            </b-select>
          </b-field>
        </template>

        <template v-else-if="row.type === 'slider'">
          <b-field
            :message="`Distance approx. : de ${getApproximateMapScaleInMetersByZoom(
              fields.publicMapZoom.value[0],
            )} à ${getApproximateMapScaleInMetersByZoom(fields.publicMapZoom.value[1])}`"
          >
            <b-slider
              v-model="fields.publicMapZoom.value"
              :min="mapZooms.at(0)"
              :max="mapZooms.at(-1)"
              :step="1"
              ticks
              :tooltip="false"
              :custom-formatter="getApproximateMapScaleInMetersByZoom"
            />
          </b-field>
        </template>
      </b-table-column>

      <b-table-column
        v-slot="{ row }"
        label="Privé"
        centered
        header-class="has-background-danger-light has-text-danger"
        width="30%"
      >
        <template v-if="row.isGroup" />
        <template v-else-if="row.type === 'switch'">
          <b-switch v-if="row.privateKey" v-model="fields[row.privateKey]" type="is-success" />
          <span v-else class="has-text-grey-lighter">—</span>
        </template>

        <template v-else-if="row.type === 'select'">
          <b-field
            :message="fields.privateDefaultMapLayer.error"
            :type="fields.privateDefaultMapLayer.error !== null ? 'is-danger' : ''"
          >
            <b-select
              v-model="fields.privateDefaultMapLayer.value"
              placeholder="Choisir"
              icon="map"
              expanded
            >
              <option v-for="name in mapLayers" :key="name" :value="name">
                {{ name }}
              </option>
            </b-select>
          </b-field>
        </template>

        <template v-else>
          <span class="has-text-grey-lighter">—</span>
        </template>
      </b-table-column>
    </b-table>

    <b-field>
      <b-button native-type="submit" type="is-primary" :loading="loading" :disabled="disabledForm">
        Modifier
      </b-button>
    </b-field>
  </form>
</template>

<script>
import { mapActions } from 'vuex'
import { MAP_APPROXIMATE_ZOOM_SCALE, MAP_LAYERS, MAP_ZOOMS } from '@constants/settings/mapLayer'
import { COORDINATE_FORMATS } from '@constants/settings/coordinate'
import {
  ALERT_TYPE_OK,
  ALERT_TYPE_MEDICAL_HELP,
  ALERT_TYPE_TECHNICAL_HELP,
  ALERT_TYPE_LOW_BATTERY,
  ALERT_TYPE_OFF_TRACK,
} from '@constants/alert/type'

const ALERT_TYPES = [
  {
    id: ALERT_TYPE_OK,
    icon: 'thumbs-up',
    label: 'OK',
  },
  {
    id: ALERT_TYPE_MEDICAL_HELP,
    icon: 'heart',
    label: 'Médicale',
  },
  {
    id: ALERT_TYPE_TECHNICAL_HELP,
    icon: 'wrench',
    label: 'Technique',
  },
  {
    id: ALERT_TYPE_LOW_BATTERY,
    icon: 'battery-half',
    label: 'Batterie',
  },
  {
    id: ALERT_TYPE_OFF_TRACK,
    icon: 'road-circle-exclamation',
    label: 'Hors zone',
  },
]

const TABLE_DATA = [
  { isGroup: true, label: 'Affichage' },
  {
    label: 'Replay',
    icon: 'play',
    type: 'switch',
    publicKey: 'publicShowReplay',
    privateKey: null,
  },
  {
    label: 'Statistiques des participants',
    icon: 'chart-bar',
    type: 'switch',
    publicKey: 'publicShowVehicleStatistics',
    privateKey: 'privateShowVehicleStatistics',
  },
  {
    label: 'Statistiques des checkpoints',
    icon: 'chart-bar',
    type: 'switch',
    publicKey: null,
    privateKey: 'privateShowCheckpointStatistics',
  },
  {
    label: 'Inversion des balises',
    icon: 'repeat',
    type: 'switch',
    publicKey: null,
    privateKey: 'privateShowSwitchTrackers',
  },

  { isGroup: true, label: 'Carte' },
  {
    label: 'Cartes disponibles',
    icon: 'layer-group',
    type: 'checkbox',
    publicKey: null,
    privateKey: null,
  },
  {
    label: 'Carte par défaut',
    icon: 'map-marker-alt',
    type: 'select',
    publicKey: null,
    privateKey: null,
  },
  {
    label: 'Rendu 3D',
    icon: 'mountain',
    type: 'switch',
    publicKey: 'publicShowMapTerrain',
    privateKey: 'privateShowMapTerrain',
  },
  {
    label: 'Niveaux de zoom',
    icon: 'search-plus',
    type: 'slider',
    publicKey: null,
    privateKey: null,
  },

  { isGroup: true, label: 'Parcours & étapes' },
  {
    label: 'Checkpoints',
    icon: 'flag-checkered',
    type: 'switch',
    publicKey: 'publicShowCheckpoints',
    privateKey: null,
  },
  {
    label: 'Trace du participant',
    icon: 'route',
    type: 'switch',
    publicKey: 'publicShowVehicleTrack',
    privateKey: null,
  },
  {
    label: 'Étapes précédentes',
    icon: 'history',
    type: 'switch',
    publicKey: 'publicShowPreviousStages',
    privateKey: null,
  },
  {
    label: "Trace avant début de l'étape",
    icon: 'hourglass-start',
    type: 'switch',
    publicKey: 'publicShowUpcomingGeography',
    privateKey: null,
  },
]

export default {
  name: 'LiveSettingsForm',

  props: {
    live: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      settings: null,
      fields: {
        globalCoordinateFormat: {
          value: '',
          error: null,
        },
        publicDefaultMapLayer: {
          value: '',
          error: null,
        },
        privateDefaultMapLayer: {
          value: '',
          error: null,
        },
        publicMapLayers: {
          value: MAP_LAYERS,
          error: null,
        },
        publicShowUpcomingGeography: false,
        publicMapZoom: {
          value: [0, 18],
          error: null,
        },
        privateAlertTypes: [],
        privateShowSwitchTrackers: false,
        globalSocialFeedId: null,
        publicShowCheckpoints: false,
        privateVehicleIdleThreshold: {
          value: null,
          error: null,
        },
        publicShowVehicleStatistics: false,
        privateShowVehicleStatistics: false,
        publicShowVehicleTrack: false,
        publicShowReplay: false,
        publicShowPreviousStages: false,
        publicShowMapTerrain: false,
        privateShowMapTerrain: false,
        privateShowCheckpointStatistics: true,
      },
    }
  },

  watch: {
    settings: {
      handler: 'initFields',
    },
    'fields.publicMapLayers.value': {
      handler(publicMapLayers) {
        this.fields.publicMapLayers.error = publicMapLayers.length
          ? null
          : 'Au moins une carte est requise'
        this.fields.publicDefaultMapLayer.error = publicMapLayers.includes(
          this.fields.publicDefaultMapLayer.value,
        )
          ? null
          : 'Une carte est requise'
      },
    },
    'fields.publicDefaultMapLayer.value': {
      handler(publicDefaultMapLayer) {
        this.fields.publicDefaultMapLayer.error = this.fields.publicMapLayers.value.includes(
          publicDefaultMapLayer,
        )
          ? null
          : 'Un fond de carte est requis'
      },
    },
    'fields.privateShowVehicleStatistics': {
      handler(privateShowVehicleStatistics) {
        if (!privateShowVehicleStatistics) {
          this.fields.publicShowVehicleStatistics = false
        }
      },
    },
    'fields.publicShowVehicleStatistics': {
      handler(publicShowVehicleStatistics) {
        if (publicShowVehicleStatistics && !this.fields.privateShowVehicleStatistics) {
          this.fields.privateShowVehicleStatistics = true
        }
      },
    },
  },

  created() {
    this.tableData = TABLE_DATA
    this.mapLayers = MAP_LAYERS
    this.mapZooms = MAP_ZOOMS
    this.alertTypes = ALERT_TYPES
    this.coordinateFormats = COORDINATE_FORMATS
  },

  async mounted() {
    await this.load()
  },

  computed: {
    disabledForm() {
      return this.loading || Object.values(this.fields).some((field) => field?.error)
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    async load() {
      this.loading = true

      try {
        this.settings = await this.$services.liveSettingsService.getByLiveId(this.live.id)
      } catch (err) {
        this.settings = null

        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
        console.error(err)
      }

      this.loading = false
    },

    initFields() {
      this.fields.globalCoordinateFormat.value = this.settings?.globalCoordinateFormat
      this.fields.publicDefaultMapLayer.value = this.settings?.publicDefaultMapLayer
      this.fields.privateDefaultMapLayer.value = this.settings?.privateDefaultMapLayer
      this.fields.publicMapLayers.value = this.settings?.publicMapLayers
      this.fields.publicShowUpcomingGeography = this.settings.publicShowUpcomingGeography
      this.fields.publicMapZoom.value = this.formatPublicMapZoom(this.settings)
      this.fields.privateAlertTypes = this.settings.privateAlertTypes
      this.fields.privateShowSwitchTrackers = this.settings.privateShowSwitchTrackers
      this.fields.publicShowCheckpoints = this.settings.publicShowCheckpoints
      this.fields.globalSocialFeedId = this.settings.globalSocialFeedId
      this.fields.privateVehicleIdleThreshold.value =
        this.settings.privateVehicleIdleThreshold > 0
          ? this.settings.privateVehicleIdleThreshold
          : null
      this.fields.publicShowVehicleStatistics = this.settings.publicShowVehicleStatistics
      this.fields.privateShowVehicleStatistics = this.settings.privateShowVehicleStatistics
      this.fields.privateShowCheckpointStatistics = this.settings.privateShowCheckpointStatistics
      this.fields.publicShowVehicleTrack = this.settings.publicShowVehicleTrack
      this.fields.publicShowReplay = this.settings.publicShowReplay
      this.fields.publicShowPreviousStages = this.settings.publicShowPreviousStages
      this.fields.publicShowMapTerrain = this.settings.publicShowMapTerrain
      this.fields.privateShowMapTerrain = this.settings.privateShowMapTerrain
    },

    async submit() {
      this.loading = true

      try {
        const { publicMapMinZoom, publicMapMaxZoom } = this.formatSettingsPublicMapZoom()

        this.settings = await this.$services.liveSettingsService.update(this.settings.id, {
          globalCoordinateFormat: this.fields.globalCoordinateFormat.value,
          publicDefaultMapLayer: this.fields.publicDefaultMapLayer.value,
          privateDefaultMapLayer: this.fields.privateDefaultMapLayer.value,
          publicMapLayers: this.fields.publicMapLayers.value,
          publicShowUpcomingGeography: this.fields.publicShowUpcomingGeography,
          publicMapMinZoom,
          publicMapMaxZoom,
          privateAlertTypes: this.fields.privateAlertTypes,
          privateShowSwitchTrackers: this.fields.privateShowSwitchTrackers,
          publicShowCheckpoints: this.fields.publicShowCheckpoints,
          globalSocialFeedId: this.fields.globalSocialFeedId || null,
          privateVehicleIdleThreshold:
            this.fields.privateVehicleIdleThreshold.value > 0
              ? this.fields.privateVehicleIdleThreshold.value
              : null,
          publicShowVehicleStatistics: this.fields.publicShowVehicleStatistics,
          privateShowVehicleStatistics: this.fields.privateShowVehicleStatistics,
          privateShowCheckpointStatistics: this.fields.privateShowCheckpointStatistics,
          publicShowVehicleTrack: this.fields.publicShowVehicleTrack,
          publicShowReplay: this.fields.publicShowReplay,
          publicShowPreviousStages: this.fields.publicShowPreviousStages,
          publicShowMapTerrain: this.fields.publicShowMapTerrain,
          privateShowMapTerrain: this.fields.privateShowMapTerrain,
        })

        this.addToastMessage({
          text: `Les configurations du live "${this.live.name}" ont été mis à jour.`,
          type: 'is-success',
        })
      } catch (err) {
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
        console.error(err)
      }

      this.loading = false
    },

    getApproximateMapScaleInMetersByZoom(zoom) {
      const meters = MAP_APPROXIMATE_ZOOM_SCALE[this.mapZooms.at(-1) - zoom]
      const kMeters = meters / 1000

      return kMeters >= 1 ? `${kMeters}km` : `${meters}m`
    },

    formatPublicMapZoom({ publicMapMinZoom, publicMapMaxZoom }) {
      const maxZoom = this.mapZooms.at(-1)
      const min = maxZoom - publicMapMaxZoom
      const max = maxZoom - publicMapMinZoom

      return [min, max]
    },

    formatSettingsPublicMapZoom() {
      const maxZoom = this.mapZooms.at(-1)
      const min = maxZoom - this.fields.publicMapZoom.value.at(-1)
      const max = maxZoom - this.fields.publicMapZoom.value.at(0)

      return {
        publicMapMinZoom: min,
        publicMapMaxZoom: max,
      }
    },
  },
}
</script>
